import React, { useEffect, useState } from 'react';
import { formatMoney, humanizeEnum } from '../../utils/format';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { WageGradeService } from '../../services/wageGrade.service';
import { IWageGrade, IWageGradeCreateRequest, IWageGradeUpdateRequest } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import WageGradeForm from './WageGradeForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import SearchableSelect from '../common/SearchableSelect';

/**
 * Wage grade catalog management (decision note 2026-09-08 wage-grades REV 2, D5), modeled on
 * {@code SalaryComponentList}. R1/C2: "Load 2023 gazette defaults" is the ONLY way this
 * institution's catalog ever gets the RMG floors -- nothing seeds them automatically.
 */
const WageGradeManagement: React.FC = () => {
    const [grades, setGrades] = useState<IWageGrade[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGrade, setEditingGrade] = useState<IWageGrade | null>(null);
    const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const loadGrades = async () => {
        if (!institutionId) return;
        try {
            const data = await WageGradeService.getByInstitution(institutionId);
            setGrades(data);
        } catch (error) {
            showApiError(error, 'Failed to load wage grades');
        }
    };

    useEffect(() => { loadGrades(); }, [institutionId]);

    const handleAdd = () => {
        setEditingGrade(null);
        setDialogOpen(true);
    };

    const handleEdit = (grade: IWageGrade) => {
        setEditingGrade(grade);
        setDialogOpen(true);
    };

    const handleSave = async (data: IWageGradeCreateRequest | IWageGradeUpdateRequest) => {
        try {
            if (editingGrade) {
                await WageGradeService.update(editingGrade.id, data as IWageGradeUpdateRequest);
                showSuccess('Wage grade updated');
            } else {
                await WageGradeService.create({ ...(data as IWageGradeCreateRequest), institutionId });
                showSuccess('Wage grade created');
            }
            setDialogOpen(false);
            loadGrades();
        } catch (error) {
            showApiError(error, 'Failed to save wage grade');
        }
    };

    const handleConfirmSeedDefaults = async () => {
        try {
            await WageGradeService.seedDefaults(institutionId);
            showSuccess('2023 gazette defaults loaded');
            setSeedConfirmOpen(false);
            loadGrades();
        } catch (error) {
            showApiError(error, 'Failed to load gazette defaults');
        }
    };

    const editStatus = (g: IWageGrade): string | null => {
        if (g.seededDefaultValue == null) return null; // MANUAL, nothing to badge
        return g.seededDefaultValue === g.minimumGrossWage ? 'Gazette' : 'Edited';
    };

    // Compact composition summary: "HR 50% · M 750 · T 450 · F 1,250"; unset parts are skipped.
    const compositionSummary = (g: IWageGrade): string => {
        const num = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
        const parts: string[] = [];
        if (g.housePercentOfBasic != null) parts.push(`HR ${g.housePercentOfBasic}%`);
        if (g.medicalAllowance != null) parts.push(`M ${num(g.medicalAllowance)}`);
        if (g.conveyanceAllowance != null) parts.push(`T ${num(g.conveyanceAllowance)}`);
        if (g.foodAllowance != null) parts.push(`F ${num(g.foodAllowance)}`);
        return parts.length ? parts.join(' · ') : '-';
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.grades.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Wage Grades</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {isSystemAdmin && (
                        <SearchableSelect
                            data-help="payroll.grades.institution"
                            label="Select Institution"
                            options={institutions}
                            value={institutionId}
                            onChange={id => setInstitutionId(id)}
                            getOptionLabel={inst => inst.name}
                            clearable={false}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    <Button data-help="payroll.grades.seedDefaults" variant="outlined" onClick={() => setSeedConfirmOpen(true)} disabled={!institutionId}>
                        Load 2023 Gazette Defaults
                    </Button>
                    <Button data-help="payroll.grades.add" variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                        Add Wage Grade
                    </Button>
                </Box>
            </Box>

            {grades.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No wage grades are configured for this institution. The Wage Compliance register
                    will report honestly that no grades exist -- click "Load 2023 Gazette Defaults" to
                    opt in, or add grades manually.
                </Alert>
            )}

            <TableContainer component={Paper} data-help-section="payroll.grades.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="payroll.grades.col.code">Code</TableCell>
                            <TableCell data-help="payroll.grades.col.name">Display Name</TableCell>
                            <TableCell align="right" data-help="payroll.grades.col.minWage">Minimum Gross Wage</TableCell>
                            <TableCell data-help="payroll.grades.col.composition">Composition</TableCell>
                            <TableCell align="right" data-help="payroll.grades.col.sortOrder">Sort Order</TableCell>
                            <TableCell data-help="payroll.grades.col.status">Status</TableCell>
                            <TableCell align="right" data-help="payroll.grades.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {grades.map((g) => (
                            <TableRow key={g.id}>
                                <TableCell>{humanizeEnum(g.code)}</TableCell>
                                <TableCell>
                                    {g.displayName}
                                    {editStatus(g) && (
                                        <Chip data-help="payroll.grades.badge" size="small" label={editStatus(g)}
                                              color={editStatus(g) === 'Edited' ? 'warning' : 'default'}
                                              sx={{ ml: 1 }} />
                                    )}
                                </TableCell>
                                <TableCell align="right">{formatMoney(g.minimumGrossWage)}</TableCell>
                                <TableCell>{compositionSummary(g)}</TableCell>
                                <TableCell align="right">{g.sortOrder}</TableCell>
                                <TableCell>
                                    <Chip label={g.isActive ? 'Active' : 'Inactive'}
                                          color={g.isActive ? 'primary' : 'default'} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton data-help="payroll.grades.action.edit" onClick={() => handleEdit(g)} size="small" aria-label="edit">
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {grades.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No wage grades found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <WageGradeForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                grade={editingGrade}
            />

            <Dialog open={seedConfirmOpen} onClose={() => setSeedConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Load 2023 Gazette Defaults</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        These six rows (Grade I-V and Learner) are figures as commonly reported for the
                        2023 Minimum Wage Board (garments) gazette and are UNVERIFIED -- confirm against
                        the official gazette/SRO before relying on them. They apply only to
                        Bangladesh RMG-sector wage structures; do not load this for a tenant this
                        statute does not cover.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        This is idempotent -- any code you have already created will not be
                        duplicated or overwritten.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSeedConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmSeedDefaults} variant="contained" color="warning">
                        Load Defaults
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WageGradeManagement;
